/**
 * デモモード（外部公開向けサンプルサイト）の状態管理ユーティリティ。
 *
 * 管理者が発行したデモリンク（/demo/{token}）を開くと、BE がデモ閲覧
 * アカウントの DRF Token を返す。FE は通常サインインと同じ Cookie
 * （auth_token）に保存しつつ、本モジュールの状態を localStorage に持つ。
 *
 * - `createAuthBaseQuery`（src/common/utils.ts）がデモ中の書き込みを
 *   送信前に遮断し、`soremo:demo-blocked` イベントを発火する
 *   （DemoBlockedModal が購読してモーダルを表示し、SoremoToaster が
 *   購読して遮断直後のトーストを抑止する）
 * - 既読・ダウンロード記録などの「閲覧しただけで走る自動書き込み」は
 *   モーダルを出さずに合成成功レスポンスで握りつぶす
 * - BE 側にも DemoReadOnlyMiddleware の安全網がある。そこから返る
 *   DEMO_READ_ONLY は同じイベントに変換し、DEMO_EXPIRED（およびデモ中の
 *   401）は createAuthBaseQuery が失効画面（/demo/:token）へ直接誘導する
 *
 * 状態を localStorage に置くのは、デモ閲覧者が新しいタブでリンクを開いても
 * ガードを維持するため（Cookie と同じブラウザスコープに揃える）。
 * 通常サインイン成功時には必ず `clearDemoMode()` を呼び、実ユーザーの
 * 操作を誤って遮断しないようにする。
 */

const STORAGE_KEY = "soremo_demo_mode";

/** デモ書き込み遮断時に発火するイベント名（DemoBlockedModal / SoremoToaster が購読） */
export const DEMO_BLOCKED_EVENT = "soremo:demo-blocked";

export interface DemoModeState {
  /** デモリンクのトークン（期限切れ時の再誘導先 /demo/{token} に使う） */
  token: string;
  /** 対象プロジェクト ID */
  projectId: string;
  /** 閲覧ロール（API 公開値: CLIENT / REVIEWER / PRODUCER / DIRECTOR / SPECIALIST） */
  position: string | null;
  /** デモ閲覧アカウントの表示名 */
  displayName: string;
}

/**
 * ポジション（API 表記）→ 表示ラベル。
 * AdminDemoLinks のメンバー一覧が使用する。
 * notes / library の POSITION_OPTIONS と同じ表記に揃えている。
 */
export const POSITION_LABELS: Record<string, string> = {
  CLIENT: "クライアント",
  REVIEWER: "レビュアー",
  PRODUCER: "プロデューサー",
  DIRECTOR: "ディレクター",
  SPECIALIST: "スペシャリスト",
  ADMIN: "管理者",
};

/**
 * 初回表示画面（DemoLink.landing_tab）→ 表示ラベル。
 * AdminDemoLinks の発行フォーム・一覧が使用する。
 * BE の DemoLink.LANDING_TAB_CHOICES と対応を保つこと。
 */
export const LANDING_TAB_LABELS: Record<string, string> = {
  home: "ホーム",
  talkroom: "トークルーム",
};

/** 現在のデモモード状態を返す（未設定・不正値は null） */
export const getDemoMode = (): DemoModeState | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DemoModeState> | null;
    if (!parsed || typeof parsed.token !== "string" || typeof parsed.projectId !== "string") {
      return null;
    }
    return {
      token: parsed.token,
      projectId: parsed.projectId,
      position: parsed.position ?? null,
      displayName: String(parsed.displayName ?? ""),
    };
  } catch {
    return null;
  }
};

/** デモモード中かどうか（createAuthBaseQuery のガード判定に使う） */
export const isDemoMode = (): boolean => getDemoMode() !== null;

/** デモモードを開始する（DemoEntry がセッション交換成功後に呼ぶ） */
export const enterDemoMode = (state: DemoModeState): void => {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

/** デモモード状態のみ破棄する（遷移しない） */
export const clearDemoMode = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage が使えない環境では何もしない
  }
};

/**
 * 遮断直後のトースト抑止ウィンドウ (ms)。
 * 書き込み遮断は DemoBlockedModal とは別に mutation の 403 reject としても
 * 伝播し、各画面の `.catch(() => toast.error("保存に失敗しました"))` が発火
 * してモーダルとトーストの二重通知になる。呼び出し箇所は多数に散らばる
 * ため、遮断時刻を起点に SoremoToaster 側で一元的に抑止する。
 */
const TOAST_SUPPRESS_MS = 2500;
let demoBlockedAt = 0;

/** デモ書き込み遮断イベントを発火する（モーダル表示のトリガー） */
export const emitDemoBlocked = (): void => {
  demoBlockedAt = Date.now();
  window.dispatchEvent(new CustomEvent(DEMO_BLOCKED_EVENT));
};

/** 遮断直後に呼び出し元が出す「保存に失敗しました」等のトーストを抑止するか */
export const isDemoToastSuppressed = (): boolean =>
  Date.now() - demoBlockedAt < TOAST_SUPPRESS_MS;

// ─── createAuthBaseQuery 用リクエストガード ─────────────────────

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/**
 * デモ中でも送信を許可する書き込みパス（認証系の入口・出口）。
 * BE の DemoReadOnlyMiddleware（DEMO_EXEMPT_PREFIXES）と対応を保つこと。
 * `/api/logout` を遮断してしまうと、デモ警告は出るのにデモから抜けられない。
 */
const DEMO_EXEMPT_PATTERNS: RegExp[] = [
  /^\/api\/demo\/session\/?$/,
  /^\/api\/login\/?$/,
  /^\/api\/logout\/?$/,
];

const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

/**
 * 「閲覧しただけで走る自動書き込み」= 既読登録・ダウンロード記録の
 * コレクション PATCH。モーダルを出さずに握りつぶす。
 * BE の DemoReadOnlyMiddleware（DEMO_SILENT_WRITE_PATTERNS）と対応を保つこと。
 */
const DEMO_SILENT_WRITE_PATTERNS: Array<{ method: string; pattern: RegExp }> = [
  // トークルーム: 既読 (message_ids) / DL 記録 (downloaded_file_ids)
  { method: "PATCH", pattern: new RegExp(`^/api/${UUID}/project_messages/?$`) },
  // ショットコメント: 既読 (comment_ids) / DL 記録
  { method: "PATCH", pattern: new RegExp(`^/api/${UUID}/comments/?$`) },
  // オファー DM: 既読 / DL 記録
  { method: "PATCH", pattern: new RegExp(`^/api/me/${UUID}/dm/${UUID}/messages/?$`) },
  // チャット DM: 既読 / DL 記録
  { method: "PATCH", pattern: new RegExp(`^/api/me/${UUID}/chats/${UUID}/messages/?$`) },
];

/** URL からクエリ文字列を除いたパス部分を取り出す */
const toPath = (url: string): string => {
  const withoutQuery = url.split("?")[0];
  // fetchBaseQuery の url は "/api/..." 形式（HOSTNAME はベース URL 側）
  if (withoutQuery.startsWith("http")) {
    try {
      return new URL(withoutQuery).pathname;
    } catch {
      return withoutQuery;
    }
  }
  return withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
};

export type DemoGuardResult =
  | { kind: "pass" }
  | { kind: "silent-success" }
  | { kind: "blocked" };

/**
 * デモモード中のリクエストを送信前に判定する。
 * - pass:           そのまま送信（GET 系・認証系の入口）
 * - silent-success: 送信せずに合成成功レスポンスを返す（既読・DL 記録）
 * - blocked:        送信せずにエラーを返し、モーダルを表示する
 */
export const guardDemoRequest = (method: string, url: string): DemoGuardResult => {
  const normalizedMethod = (method || "GET").toUpperCase();
  if (SAFE_METHODS.has(normalizedMethod)) {
    return { kind: "pass" };
  }

  const path = toPath(url);
  if (DEMO_EXEMPT_PATTERNS.some((pattern) => pattern.test(path))) {
    return { kind: "pass" };
  }
  if (
    DEMO_SILENT_WRITE_PATTERNS.some(
      ({ method: m, pattern }) => m === normalizedMethod && pattern.test(path),
    )
  ) {
    return { kind: "silent-success" };
  }
  return { kind: "blocked" };
};

/**
 * BE 安全網（DemoReadOnlyMiddleware）からのエラー応答を分類する。
 * 別タブ等で localStorage フラグが無いままデモ token で書き込みが飛んだ
 * 場合や、リンク失効の検知に使う（処理は createAuthBaseQuery 側で行う）。
 *
 * 認証系の入口（/api/demo/session・/api/login）のエラーは呼び出し元
 * （DemoEntry の失効画面など）が自前で処理するため対象外とする。
 * 対象にしてしまうと、デモ閲覧中に別の失効リンクを開いた際、
 * 失効処理 → 旧トークンで再入場となり失効画面が出ない。
 */
export const classifyDemoError = (
  error: unknown,
  url?: string,
): "blocked" | "expired" | null => {
  if (!error || typeof error !== "object") return null;
  if (url !== undefined) {
    const path = toPath(url);
    if (DEMO_EXEMPT_PATTERNS.some((pattern) => pattern.test(path))) {
      return null;
    }
  }
  const data = (error as { data?: { code?: string } }).data;
  const code = data?.code;
  if (code === "DEMO_READ_ONLY") return "blocked";
  if (code === "DEMO_EXPIRED") return "expired";
  // 管理者がリンクを無効化・削除すると DRF Token ごと失効し、BE からは
  // code なしの素の 401（Invalid token）が返る。デモ中の 401 はすべて
  // セッション終了とみなし、失効画面へ誘導する。
  if (isDemoMode() && (error as { status?: number }).status === 401) {
    return "expired";
  }
  return null;
};
